import { useEffect, useRef } from "react";
import { gsap } from "gsap";

export default function Cursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  useEffect(() => {
    // Skip on touch devices
    if (typeof window === "undefined") return;
    const isTouch = window.matchMedia("(hover: none)").matches;
    if (isTouch) return;

    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    const pos = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const ringPos = { x: pos.x, y: pos.y };

    gsap.set([dot, ring], { xPercent: -50, yPercent: -50, x: pos.x, y: pos.y, opacity: 0 });

    const xToDot = gsap.quickTo(dot, "x", { duration: 0.15, ease: "power3.out" });
    const yToDot = gsap.quickTo(dot, "y", { duration: 0.15, ease: "power3.out" });
    const xToRing = gsap.quickTo(ring, "x", { duration: 0.55, ease: "power3.out" });
    const yToRing = gsap.quickTo(ring, "y", { duration: 0.55, ease: "power3.out" });

    let firstMove = true;
    const onMove = (e) => {
      pos.x = e.clientX;
      pos.y = e.clientY;
      xToDot(pos.x);
      yToDot(pos.y);
      xToRing(pos.x);
      yToRing(pos.y);
      if (firstMove) {
        gsap.to([dot, ring], { opacity: 1, duration: 0.4 });
        firstMove = false;
      }
    };

    const hoverableSel = 'a, button, [role="button"], input, textarea, select, [data-cursor-hover]';
    const onOver = (e) => {
      if (e.target.closest(hoverableSel)) {
        gsap.to(ring, { scale: 2.4, borderColor: "var(--gicp-gold)", duration: 0.35, ease: "power3.out" });
        gsap.to(dot, { scale: 0, duration: 0.25, ease: "power3.out" });
      }
    };
    const onOut = (e) => {
      if (e.target.closest(hoverableSel) && !e.relatedTarget?.closest(hoverableSel)) {
        gsap.to(ring, { scale: 1, borderColor: "rgba(253,251,247,0.5)", duration: 0.35, ease: "power3.out" });
        gsap.to(dot, { scale: 1, duration: 0.25, ease: "power3.out" });
      }
    };

    const onLeave = () => gsap.to([dot, ring], { opacity: 0, duration: 0.2 });
    const onEnter = () => gsap.to([dot, ring], { opacity: 1, duration: 0.2 });

    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    return () => {
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
    };
  }, []);

  return (
    <>
      <div
        ref={ringRef}
        aria-hidden="true"
        data-testid="custom-cursor-ring"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 34,
          height: 34,
          borderRadius: "999px",
          border: "1px solid rgba(253,251,247,0.5)",
          pointerEvents: "none",
          zIndex: 9999,
          mixBlendMode: "difference",
          willChange: "transform",
        }}
      />
      <div
        ref={dotRef}
        aria-hidden="true"
        data-testid="custom-cursor-dot"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 6,
          height: 6,
          borderRadius: "999px",
          background: "var(--gicp-gold)",
          pointerEvents: "none",
          zIndex: 9999,
          willChange: "transform",
        }}
      />
    </>
  );
}
